import { useState, useEffect } from "react";
import { Modal, Textarea, Button, Group, Stack, Text } from "@mantine/core";
import type { DriverAccountStatus } from "../../types/domain";

const COPY: Record<string, { title: string; color: string; verb: string }> = {
  suspended: { title: "Suspend driver", color: "orange", verb: "Suspend" },
  rejected: { title: "Reject driver", color: "red", verb: "Reject" },
  approved: { title: "Approve driver", color: "green", verb: "Approve" },
  deleted: { title: "Delete driver", color: "red", verb: "Delete" },
};

// Reason is required for reject/suspend (backend enforces min length 5).
export function StatusChangeModal({
  status,
  onClose,
  loading,
  onConfirm,
}: {
  status: DriverAccountStatus | null;
  onClose: () => void;
  loading: boolean;
  onConfirm: (status: DriverAccountStatus, reason: string) => void;
}) {
  const [reason, setReason] = useState("");
  useEffect(() => {
    if (status) setReason("");
  }, [status]);

  if (!status) return null;
  const copy = COPY[status] ?? {
    title: "Update status",
    color: "blue",
    verb: "Update",
  };
  const tooShort = reason.trim().length < 5;

  return (
    <Modal opened={!!status} onClose={onClose} title={copy.title} centered>
      <Stack>
        <Text size="sm" c="dimmed">
          Provide a reason (min. 5 characters). The driver is notified and this
          is recorded in their status history.
        </Text>
        <Textarea
          label="Reason"
          placeholder="Explain the reason for this change…"
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
          autosize
          minRows={3}
          required
          error={reason.length > 0 && tooShort ? "Too short" : undefined}
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            color={copy.color}
            loading={loading}
            disabled={tooShort}
            onClick={() => onConfirm(status, reason.trim())}
          >
            {copy.verb}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
